import React, { useState, useEffect } from 'react';
import { fetchTransactions } from '../services/transactionService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { ShimmerTable, ShimmerBox } from '../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';

const Transactions = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchTransactions(token)
      .then(setTransactions)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const filteredTransactions = transactions.filter(
    (txn) =>
      txn.user_email?.toLowerCase().includes(search.toLowerCase()) ||
      txn.merchant_name?.toLowerCase().includes(search.toLowerCase()) ||
      txn.type?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title={t('transactions')} />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Input
          type="text"
          placeholder={t('searchByUserMerchantType')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-64"
        />
      </div>
      {loading ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <ShimmerBox width="w-64" height="h-10" />
            <ShimmerBox width="w-32" height="h-10" />
          </div>
          <ShimmerTable rows={8} columns={7} />
        </div>
      ) : error ? (
        <div className="py-8 text-center text-destructive">{error}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('id')}</TableHead>
                  <TableHead>{t('user')}</TableHead>
                  <TableHead>{t('merchant')}</TableHead>
                  <TableHead>{t('amount')}</TableHead>
                  <TableHead>{t('type')}</TableHead>
                  <TableHead>{t('date')}</TableHead>
                  <TableHead>{t('status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                      {search ? t('noResultsFound') : t('noTransactionsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell>{txn.id}</TableCell>
                      <TableCell>{txn.user_email || '-'}</TableCell>
                      <TableCell>{txn.merchant_name || '-'}</TableCell>
                      <TableCell>{txn.amount}</TableCell>
                      <TableCell>{txn.type}</TableCell>
                      <TableCell>
                        {txn.date ? new Date(txn.date).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={txn.status === 'success' ? 'success' : 'destructive'}>
                          {txn.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Transactions;
